import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

import EditIcon from '@mui/icons-material/Edit';

import Config from "../config/Config.js";
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import ProjectCard from '../components/project/ProjectCard';
import UserCard from '../components/user/UserCard';
import SweetLoadingSpinner from "../components/eyeCandy/SweetLoadingSpinner.js";

const tabStyle = (active) => ({
    fontSize: 14, fontWeight: 600, textDecoration: 'none',
    padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
    border: 'none', background: active ? '#59c6c1' : 'transparent',
    color: active ? '#0f172a' : 'var(--muted)',
    transition: 'all 100ms ease',
});

const subTabStyle = (active) => ({
    fontSize: 13, fontWeight: 600, textDecoration: 'none',
    padding: '3px 10px', borderRadius: 5, cursor: 'pointer',
    border: active ? '1px solid var(--border)' : '1px solid transparent',
    background: active ? 'var(--surface)' : 'transparent',
    color: active ? 'var(--ink)' : 'var(--muted)',
    transition: 'all 100ms ease',
});

const countBadge = {
    fontSize: 12, fontWeight: 600,
    color: 'var(--ink)',
    marginLeft: 6,
    opacity: 0.5,
};

export default function Home() {
    const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
    const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

    const [extendedUser, setExtendedUser] = useState({});
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [topTab, setTopTab] = useState('projects');
    const [projectTab, setProjectTab] = useState('private');

    useEffect(() => {
        async function loadAll() {
            try {
                const [profileRes, firstPage] = await Promise.all([
                    axios.get(`${Config().extendedUserUrl}${user.pk}/`),
                    axios.get(`${Config().baseUrl}?owner=${user.pk}&limit=500`),
                ]);
                setExtendedUser(profileRes.data);
                let all = firstPage.data.results;
                let next = firstPage.data.next?.replace('http://', 'https://');
                while (next) {
                    const page = await axios.get(next);
                    all = [...all, ...page.data.results];
                    next = page.data.next?.replace('http://', 'https://');
                }
                setProjects(all);

            } catch (err) {
                console.error('Home load error:', err);
            } finally {
                setLoading(false);
            }
        }
        loadAll();
    }, [user.pk]);

    const q = filter.toLowerCase();
    const matchFilter = (p) => !q ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.subtitle || '').toLowerCase().includes(q) ||
        (p.technology || '').toLowerCase().includes(q) ||
        (p.skill || '').toLowerCase().includes(q);

    const privateProjects = projects.filter(p => !p.publish && matchFilter(p));
    const publishedProjects = projects.filter(p => p.publish && matchFilter(p));
    const hasCv = !!(extendedUser?.cv);

    const activeList = projectTab === 'private' ? privateProjects : publishedProjects;

    return (
        <div className="container" style={{ paddingTop: '50px' }}>
            <div className="row">
                <div className="col-md-3 col-sm-3 col-xs-12">
                    <SweetLoadingSpinner loadingState={loading} />
                    <UserCard user={user} extended_user={extendedUser} settings={true} back_home={false} view_profile={true} />
                </div>
                <div className="col-md-9 col-sm-9">
                    {/* Top-level tabs */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <button type="button" style={tabStyle(topTab === 'projects')} onClick={() => setTopTab('projects')}>
                            Projects
                        </button>
                        {hasCv && (
                            <button type="button" style={tabStyle(topTab === 'cv')} onClick={() => setTopTab('cv')}>
                                CV
                            </button>
                        )}
                    </div>
                    <hr style={{ marginTop: 8 }} />

                    {/* Projects section */}
                    {topTab === 'projects' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                {/* Sub-tabs */}
                                <button type="button" style={subTabStyle(projectTab === 'private')} onClick={() => setProjectTab('private')}>
                                    Private
                                    <span style={countBadge}>{privateProjects.length}</span>
                                </button>
                                <button type="button" style={subTabStyle(projectTab === 'published')} onClick={() => setProjectTab('published')}>
                                    Published
                                    <span style={countBadge}>{publishedProjects.length}</span>
                                </button>
                                <a
                                    href="/projects/new"
                                    style={{
                                        marginLeft: 'auto',
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 12, color: 'var(--muted)', textDecoration: 'none',
                                    }}
                                >
                                    + New project
                                </a>
                            </div>

                            {/* Filter */}
                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter projects..."
                                style={{
                                    width: '100%', padding: '8px 12px', marginBottom: 12,
                                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                                    background: 'var(--surface)', color: 'var(--ink)', fontSize: 14,
                                }}
                            />

                            {loading ? (
                                <SweetLoadingSpinner loadingState={true} />
                            ) : activeList.length === 0 ? (
                                <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>
                                    {filter
                                        ? `No ${projectTab} projects matching "${filter}".`
                                        : projectTab === 'private'
                                            ? 'No private projects. Hit + to start a new project.'
                                            : 'No published projects yet.'
                                    }
                                </div>
                            ) : (
                                <div className="row">
                                    {activeList.map(project => (
                                        <ProjectCard project={project} key={project.id} />
                                    ))}
                                </div>
                            )}

                        </>
                    )}

                    {/* CV section */}
                    {topTab === 'cv' && hasCv && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <a
                                    href="/settings/cv"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 12, color: 'var(--muted)', textDecoration: 'none',
                                    }}
                                >
                                    <EditIcon style={{ fontSize: 14 }} /> Edit CV
                                </a>
                            </div>
                            <div className="custom-body-markdown" style={{ fontSize: 14, lineHeight: 1.7 }}>
                                <ReactMarkdown>{extendedUser.cv}</ReactMarkdown>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
